import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { UserProfile, Wallet } from '@/types';
import type { LoginResult } from '@/services';

/**
 * 登入狀態 Store
 * -----------------------------------------------------------------------------
 * 前端對於 auth 的唯一鏡射;實際登入流程由 ``services.authProvider`` 執行,
 * store 只負責保存結果。Round 12 起認證走 HttpOnly cookie;**R42.5 砍 `accessToken`**
 * (對齊 backend MR !26 收尾 — BE envelope `access_token` 欄位砍 + Pydantic
 * `extra="forbid"`;既有 `accessToken` 為 informational state 沒實際 caller,
 * `setAccessToken` 也是死代碼,無人 call)。
 *
 * 持久化策略:
 *   - ``creativeUid`` / ``user`` / ``wallet`` 寫進 ``sessionStorage``(per-tab)。
 *     重新整理仍記得使用者;關閉分頁則清除(cookie 有獨立壽命)。
 *   - ``isNewUser`` / ``hydrating`` 不持久化(每次重新整理重新計算)。
 *
 * **R42.5 sessionStorage backward-compat**:既有 sessionStorage 內含 `accessToken`
 * 欄位的 stale shape,zustand persist 讀進來會被忽略(初始 state 沒這欄位 → merge
 * 後 drop)— 用戶不需 force re-login。
 */
export interface AuthState {
  /** 對外的使用者識別碼(permission-service 內部 user_id 永不暴露給前端)。 */
  creativeUid: string | null;
  user: UserProfile | null;
  wallet: Wallet | null;
  isNewUser: boolean;
  /** ``true`` 表示尚未跑完 bootstrap(首次 ``/login_status`` 探查),UI 應顯示 loading。 */
  hydrating: boolean;

  /** 登入成功後一次性寫入 creativeUid / user / wallet / isNewUser。 */
  setSession: (result: LoginResult) => void;
  /** profile 異動(M-16 個人頁改名 / 語系 / 時區 / 頭像)後更新鏡射 — TopNav 等即時反映。 */
  setUser: (user: UserProfile) => void;
  /** 後續 wallet 異動(topup / consume)時更新顯示用。 */
  setWallet: (wallet: Wallet) => void;
  /** 登出 / token 失效時清光所有狀態。 */
  clear: () => void;
  /** bootstrap 探查結束時呼叫,UI 會解除 loading。 */
  finishHydrating: () => void;
}

const initialState = {
  creativeUid: null as string | null,
  user: null as UserProfile | null,
  wallet: null as Wallet | null,
  isNewUser: false,
  hydrating: true,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...initialState,
      setSession: (result) =>
        set({
          creativeUid: result.creativeUid,
          user: result.user,
          wallet: result.wallet,
          isNewUser: result.isNewUser,
        }),
      setUser: (user) => set({ user }),
      setWallet: (wallet) => set({ wallet }),
      clear: () =>
        set({
          creativeUid: null,
          user: null,
          wallet: null,
          isNewUser: false,
        }),
      finishHydrating: () => set({ hydrating: false }),
    }),
    {
      name: 'hai.auth',
      storage: createJSONStorage(() => sessionStorage),
      /** 只持久化登入結果欄位;hydrating / isNewUser 永遠重新計算。 */
      partialize: (state) => ({
        creativeUid: state.creativeUid,
        user: state.user,
        wallet: state.wallet,
      }),
    },
  ),
);
